import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  CheckCircle2,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  X,
} from 'lucide-react';
import { Card, CardContent } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Checkbox } from '@/app/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Badge } from '@/app/components/ui/badge';
import { Progress } from '@/app/components/ui/progress';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/app/components/ui/command';
import { toast } from '@/app/components/ui/simple-toaster';
import {
  useSetupWizardProfile,
  useUpdateSetupWizard,
  useCompleteSetupWizard,
} from '@/hooks/use-setup-wizard';
import { useTechProfiles } from '@/hooks/use-crew';
import type {
  CompanyComplianceProfile,
  UpdateCompanyComplianceProfilePayload,
  TechProfileListItem,
} from '@/lib/types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SECTIONS = [
  { key: 'company_identity', label: 'Company Identity', number: 1 },
  { key: 'nominated_personnel', label: 'Nominated Personnel', number: 2 },
  { key: 'operations_supervision', label: 'Operations & Supervision', number: 3 },
  { key: 'job_planning', label: 'Job Planning & Communication', number: 4 },
  { key: 'equipment_management', label: 'Equipment Management', number: 5 },
  { key: 'emergency_incident', label: 'Emergency & Incident Management', number: 6 },
  { key: 'compliance_legal', label: 'Compliance & Legal', number: 7 },
  { key: 'review_audit', label: 'Review & Audit', number: 8 },
] as const;

const SCOPE_OPTIONS = [
  'Inspection', 'Maintenance', 'Cleaning', 'NDT', 'Painting',
  'Construction', 'Training', 'Other',
];


const ENVIRONMENT_OPTIONS = [
  'Commercial Buildings', 'Industrial/Oil & Gas', 'Infrastructure/Bridges',
  'Marine/Offshore', 'Wind Energy', 'Confined Spaces', 'Other',
];

const PRE_JOB_DOCUMENT_OPTIONS = [
  'Risk Assessment', 'Method Statement', 'Rescue Plan',
  'Work Pack', 'Daily Safety Briefing', 'Toolbox Talk',
];

const IRATA_LEVEL_OPTIONS = ['Level 1', 'Level 2', 'Level 3'];

const COMPLIANCE_SCOPE_LABELS = {
  is_operator: 'Operator',
  is_trainer: 'Trainer',
  operates_venues: 'Operates training venues',
};

const COUNTRY_OPTIONS = [
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'AU', label: 'Australia' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SG', label: 'Singapore' },
  { value: 'NO', label: 'Norway' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'CA', label: 'Canada' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'QA', label: 'Qatar' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'BR', label: 'Brazil' },
  { value: 'XX', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Section validation — a section is "complete" only when its key required
// fields have been filled in by the user.
// ---------------------------------------------------------------------------

function isSectionDataValid(
  sectionNumber: number,
  data: UpdateCompanyComplianceProfilePayload,
): boolean {
  const s = (v: unknown) => typeof v === 'string' && v.trim().length > 0;
  const arr = (v: unknown) => Array.isArray(v) && v.length > 0;

  switch (sectionNumber) {
    case 1:
      return s(data.company_legal_name) && s(data.country_of_operation);
    case 2:
      return s(data.ramr_name) && s(data.technical_authority_name);
    case 3:
      return arr(data.technician_irata_levels);
    case 4:
      return arr(data.pre_job_documents);
    case 5:
      return s(data.equipment_selection_process);
    case 6:
      return s(data.incident_reporting_process);
    case 7:
      return arr(data.applicable_legislation) &&
        (data.applicable_legislation as string[]).some((v) => v.trim().length > 0);
    case 8:
      return s(data.internal_auditor);
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function SetupWizardPage() {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useSetupWizardProfile();
  const updateMutation = useUpdateSetupWizard();
  const completeMutation = useCompleteSetupWizard();
  const [currentSection, setCurrentSection] = useState<number | null>(null);
  const sectionDataRef = useRef<(() => UpdateCompanyComplianceProfilePayload) | null>(null);
  const completedSectionsRef = useRef<number[]>([]);
  const refInitialised = useRef(false);

  useEffect(() => {
    if (profile && !refInitialised.current) {
      completedSectionsRef.current = profile.wizard_completed_sections ?? [];
      refInitialised.current = true;
    }
  }, [profile]);

  useEffect(() => {
    if (profile && currentSection === null) {
      const saved = profile.wizard_current_section;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentSection(saved >= 1 ? saved - 1 : 0);
    }
  }, [profile, currentSection]);

  const handleSaveSection = useCallback(
    (data: UpdateCompanyComplianceProfilePayload) => {
      const sectionNumber = (currentSection ?? 0) + 1;
      const completed = completedSectionsRef.current;
      const valid = isSectionDataValid(sectionNumber, data);
      const updatedCompleted = valid
        ? completed.includes(sectionNumber)
          ? completed
          : [...completed, sectionNumber]
        : completed.filter((n) => n !== sectionNumber);
      updateMutation.mutate(
        {
          ...data,
          wizard_status: 'in_progress',
          wizard_current_section: sectionNumber,
          wizard_completed_sections: updatedCompleted,
        },
        {
          onSuccess: () => {
            completedSectionsRef.current = updatedCompleted;
            toast.success('Section saved successfully');
          },
          onError: () => {
            toast.error('Failed to save section. Please try again.');
          },
        }
      );
    },
    [updateMutation, currentSection]
  );

  const handleComplete = useCallback(() => {
    const sectionNumber = (currentSection ?? 0) + 1;
    const completed = completedSectionsRef.current;
    const getCurrentData = sectionDataRef.current;
    const sectionData = getCurrentData ? getCurrentData() : {};
    const valid = isSectionDataValid(sectionNumber, sectionData);
    const updatedCompleted = valid
      ? completed.includes(sectionNumber)
        ? completed
        : [...completed, sectionNumber]
      : completed.filter((n) => n !== sectionNumber);
    if (getCurrentData) {
      updateMutation.mutate(
        {
          ...sectionData,
          wizard_status: 'in_progress',
          wizard_completed_sections: updatedCompleted,
        },
        {
          onSuccess: () => {
            completedSectionsRef.current = updatedCompleted;
            completeMutation.mutate(undefined, {
              onSuccess: () => {
                navigate('/compliance/generated-documents');
              },
            });
          },
        }
      );
    } else {
      completeMutation.mutate(undefined, {
        onSuccess: () => {
          navigate('/compliance/generated-documents');
        },
      });
    }
  }, [completeMutation, updateMutation, navigate, currentSection]);

  const handleNext = useCallback(() => {
    if ((currentSection ?? 0) < SECTIONS.length - 1) {
      setCurrentSection((s) => (s ?? 0) + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSection]);

  const handlePrev = useCallback(() => {
    if ((currentSection ?? 0) > 0) {
      setCurrentSection((s) => (s ?? 0) - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentSection]);

  if (isLoading || !profile || currentSection === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const completedCount = (profile.wizard_completed_sections ?? []).length;
  const progressPercent = (completedCount / SECTIONS.length) * 100;
  const isLastSection = currentSection === SECTIONS.length - 1;

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">H3Comply Setup Wizard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Complete all 8 sections to generate your compliance documents
        </p>
      </div>

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Section {currentSection + 1} of {SECTIONS.length}:{' '}
            <span className="font-medium">{SECTIONS[currentSection].label}</span>
          </span>
          <span>{Math.round(progressPercent)}% complete</span>
        </div>
        <Progress value={progressPercent} className="h-2" />
      </div>

      {/* Section navigation pills */}
      <div className="flex flex-wrap gap-2">
        {SECTIONS.map((section, idx) => {
          const isCompleted = (profile.wizard_completed_sections ?? []).includes(idx + 1);
          return (
            <button
              key={section.key}
              onClick={() => setCurrentSection(idx)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                idx === currentSection
                  ? 'bg-blue-600 text-white'
                  : isCompleted
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {isCompleted && idx !== currentSection && <CheckCircle2 className="h-3.5 w-3.5" />}
              {section.number}. {section.label}
            </button>
          );
        })}
      </div>

      {/* Section content */}
      <Card>
        <CardContent className="pt-6">
          <SectionContent
            sectionIndex={currentSection}
            profile={profile}
            onSave={handleSaveSection}
            isSaving={updateMutation.isPending}
            sectionDataRef={sectionDataRef}
          />
        </CardContent>
      </Card>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handlePrev}
          disabled={currentSection === 0}
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Previous
        </Button>

        <div className="flex gap-2">
          {updateMutation.isPending && (
            <span className="flex items-center text-sm text-gray-500">
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              Saving...
            </span>
          )}
        </div>

        {isLastSection ? (
          <Button
            onClick={handleComplete}
            disabled={completeMutation.isPending || updateMutation.isPending}
          >
            {completeMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Documents...
              </>
            ) : (
              'Complete Wizard & Generate Documents'
            )}
          </Button>
        ) : (
          <Button onClick={handleNext}>
            Next
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section Content Router
// ---------------------------------------------------------------------------

interface SectionProps {
  profile: CompanyComplianceProfile;
  onSave: (data: UpdateCompanyComplianceProfilePayload) => void;
  isSaving: boolean;
  sectionDataRef: React.MutableRefObject<(() => UpdateCompanyComplianceProfilePayload) | null>;
}

function SectionContent({
  sectionIndex,
  profile,
  onSave,
  isSaving,
  sectionDataRef,
}: {
  sectionIndex: number;
  profile: CompanyComplianceProfile;
  onSave: (data: UpdateCompanyComplianceProfilePayload) => void;
  isSaving: boolean;
  sectionDataRef: React.MutableRefObject<(() => UpdateCompanyComplianceProfilePayload) | null>;
}) {
  switch (sectionIndex) {
    case 0:
      return <Section1CompanyIdentity profile={profile} onSave={onSave} isSaving={isSaving} sectionDataRef={sectionDataRef} />;
    case 1:
      return <Section2NominatedPersonnel profile={profile} onSave={onSave} isSaving={isSaving} sectionDataRef={sectionDataRef} />;
    case 2:
      return <Section3Operations profile={profile} onSave={onSave} isSaving={isSaving} sectionDataRef={sectionDataRef} />;
    case 3:
      return <Section4JobPlanning profile={profile} onSave={onSave} isSaving={isSaving} sectionDataRef={sectionDataRef} />;
    case 4:
      return <Section5Equipment profile={profile} onSave={onSave} isSaving={isSaving} sectionDataRef={sectionDataRef} />;
    case 5:
      return <Section6Emergency profile={profile} onSave={onSave} isSaving={isSaving} sectionDataRef={sectionDataRef} />;
    case 6:
      return <Section7Legal profile={profile} onSave={onSave} isSaving={isSaving} sectionDataRef={sectionDataRef} />;
    case 7:
      return <Section8Review profile={profile} onSave={onSave} isSaving={isSaving} sectionDataRef={sectionDataRef} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Shared field helpers
// ---------------------------------------------------------------------------

function FieldGroup({ label, description, children }: { label: React.ReactNode; description?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-gray-900">{label}</Label>
      {description && <p className="text-xs text-gray-500">{description}</p>}
      {children}
    </div>
  );
}

function RepeatableField({
  label,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}) {
  const addItem = () => onChange([...values, '']);
  const removeItem = (idx: number) => onChange(values.filter((_, i) => i !== idx));
  const updateItem = (idx: number, val: string) =>
    onChange(values.map((v, i) => (i === idx ? val : v)));

  return (
    <FieldGroup label={label}>
      <div className="space-y-2">
        {values.map((val, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input
              value={val}
              onChange={(e) => updateItem(idx, e.target.value)}
              placeholder={placeholder}
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => removeItem(idx)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addItem}>
          <Plus className="mr-1 h-4 w-4" /> Add
        </Button>
      </div>
    </FieldGroup>
  );
}

function MultiCheckboxField({
  label,
  options,
  selected,
  onChange,
  description,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  description?: string;
}) {
  const toggle = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <FieldGroup label={label} description={description}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {options.map((option) => (
          <label
            key={option}
            className="flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-gray-50"
          >
            <Checkbox
              checked={selected.includes(option)}
              onCheckedChange={() => toggle(option)}
            />
            {option}
          </label>
        ))}
      </div>
    </FieldGroup>
  );
}

function PersonnelField({
  label,
  nameValue,
  onNameChange,
  irataLevel,
  onIrataLevelChange,
  description,
}: {
  label: string;
  nameValue: string;
  onNameChange: (name: string) => void;
  irataLevel?: string;
  onIrataLevelChange?: (level: string) => void;
  description?: string;
}) {
  return (
    <FieldGroup label={label} description={description}>
      <div className="flex gap-2">
        <Input
          value={nameValue}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Name"
          className="flex-1"
        />
        {onIrataLevelChange && (
          <Select value={irataLevel || ''} onValueChange={onIrataLevelChange}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="IRATA Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Level 1">Level 1</SelectItem>
              <SelectItem value="Level 2">Level 2</SelectItem>
              <SelectItem value="Level 3">Level 3</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
    </FieldGroup>
  );
}

// ---------------------------------------------------------------------------
// People Search Combobox — Fix 1
// ---------------------------------------------------------------------------

function PeopleCombobox({
  label,
  nameValue,
  onSelect,
  techProfiles,
  description,
  required,
  irataLevel,
  onIrataLevelChange,
}: {
  label: string;
  nameValue: string;
  onSelect: (name: string, techProfileId: number | null) => void;
  techProfiles: TechProfileListItem[];
  description?: string;
  required?: boolean;
  irataLevel?: string;
  onIrataLevelChange?: (level: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [manualMode, setManualMode] = useState(
    () => !!(nameValue && !techProfiles.some((tp) => `${tp.first_name} ${tp.last_name}` === nameValue))
  );
  const [manualName, setManualName] = useState(nameValue);

  const handleSelectPerson = (tp: TechProfileListItem) => {
    const fullName = `${tp.first_name} ${tp.last_name}`;
    onSelect(fullName, tp.id);
    setManualMode(false);
    setOpen(false);
  };

  const handleManualEntry = () => {
    setManualName(nameValue);
    setManualMode(true);
    setOpen(false);
  };

  const handleManualChange = (value: string) => {
    setManualName(value);
    onSelect(value, null);
  };

  return (
    <FieldGroup
      label={
        <span>
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </span>
      }
      description={description}
    >
      <div className="flex gap-2">
        {manualMode ? (
          <div className="flex flex-1 gap-2">
            <Input
              value={manualName}
              onChange={(e) => handleManualChange(e.target.value)}
              placeholder="Enter name manually"
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setManualMode(false);
                setOpen(true);
              }}
              className="shrink-0"
              title="Switch to People list"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className="flex-1 justify-between font-normal"
              >
                {nameValue || 'Select from People list...'}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search people..." />
                <CommandList>
                  <CommandEmpty>No people found.</CommandEmpty>
                  <CommandGroup heading="People">
                    {techProfiles.map((tp) => {
                      const fullName = `${tp.first_name} ${tp.last_name}`;
                      return (
                        <CommandItem
                          key={tp.id}
                          value={fullName}
                          onSelect={() => handleSelectPerson(tp)}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              nameValue === fullName ? 'opacity-100' : 'opacity-0'
                            }`}
                          />
                          <div>
                            <span className="font-medium">{fullName}</span>
                            {tp.job_title && (
                              <span className="ml-2 text-xs text-gray-500">{tp.job_title}</span>
                            )}
                          </div>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem onSelect={handleManualEntry}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Enter name manually
                    </CommandItem>
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
        {onIrataLevelChange && (
          <Select value={irataLevel || ''} onValueChange={onIrataLevelChange}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="IRATA Level" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Level 1">Level 1</SelectItem>
              <SelectItem value="Level 2">Level 2</SelectItem>
              <SelectItem value="Level 3">Level 3</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>
      {required && (
        <Badge variant="outline" className="mt-1 border-red-200 bg-red-50 text-red-700 text-xs">
          Required for IRATA
        </Badge>
      )}
    </FieldGroup>
  );
}

// ---------------------------------------------------------------------------
// Section 1 — Company Identity
// ---------------------------------------------------------------------------

function Section1CompanyIdentity({ profile, onSave, isSaving, sectionDataRef }: SectionProps) {
  const [data, setData] = useState({
    company_legal_name: profile.company_legal_name || '',
    trading_name: profile.trading_name || '',
    irata_membership_number: profile.irata_membership_number || '',
    country_of_operation: profile.country_of_operation || '',
    office_locations: profile.office_locations || [''],
    primary_scope: profile.primary_scope || [],
    work_environments: profile.work_environments || [],
    legal_entity_registered_name: profile.legal_entity_registered_name || '',
    legal_entity_registration_number: profile.legal_entity_registration_number || '',
    legal_entity_country: profile.legal_entity_country || '',
  });
  const [scopeOtherText, setScopeOtherText] = useState(
    () => {
      const otherEntry = profile.primary_scope?.find((s) => s.startsWith('Other:'));
      return otherEntry ? otherEntry.replace('Other: ', '') : '';
    }
  );

  const activeScopeLabels = (Object.keys(COMPLIANCE_SCOPE_LABELS) as Array<keyof typeof COMPLIANCE_SCOPE_LABELS>)
    .filter((key) => profile[key])
    .map((key) => COMPLIANCE_SCOPE_LABELS[key]);

  useEffect(() => { sectionDataRef.current = () => data; }, [data, sectionDataRef]);

  const handleSave = () => onSave(data);

  const handleScopeChange = (selected: string[]) => {
    const hasOther = selected.includes('Other');
    if (hasOther && scopeOtherText) {
      const withoutOtherEntries = selected.filter((s) => s !== 'Other' && !s.startsWith('Other:'));
      setData({ ...data, primary_scope: [...withoutOtherEntries, `Other: ${scopeOtherText}`] });
    } else {
      setData({ ...data, primary_scope: selected.filter((s) => !s.startsWith('Other:')) });
    }
  };

  const handleOtherTextChange = (text: string) => {
    setScopeOtherText(text);
    const withoutOtherEntries = data.primary_scope.filter((s) => s !== 'Other' && !s.startsWith('Other:'));
    if (text) {
      setData({ ...data, primary_scope: [...withoutOtherEntries, `Other: ${text}`] });
    } else {
      setData({ ...data, primary_scope: [...withoutOtherEntries, 'Other'] });
    }
  };

  const scopeHasOther = data.primary_scope.some((s) => s === 'Other' || s.startsWith('Other:'));
  const displayScope = data.primary_scope.map((s) => (s.startsWith('Other:') ? 'Other' : s));

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Section 1 — Company Identity</h2>

      <FieldGroup label="Q1. Company legal name">
        <Input
          value={data.company_legal_name}
          onChange={(e) => setData({ ...data, company_legal_name: e.target.value })}
          placeholder="Enter your company's legal name"
        />
      </FieldGroup>

      <FieldGroup label="Q2. Trading name (if different)">
        <Input
          value={data.trading_name}
          onChange={(e) => setData({ ...data, trading_name: e.target.value })}
          placeholder="Trading name"
        />
      </FieldGroup>

      <FieldGroup label="Q3. IRATA membership number">
        <Input
          value={data.irata_membership_number}
          onChange={(e) => setData({ ...data, irata_membership_number: e.target.value })}
          placeholder="e.g. 1234/56"
        />
      </FieldGroup>

      <FieldGroup label="Q4. IRATA/SPRAT compliance scope">
        <div className="flex flex-wrap gap-2">
          {activeScopeLabels.length > 0 ? (
            activeScopeLabels.map((label) => (
              <Badge key={label} variant="secondary" className="bg-blue-100 text-blue-800">
                {label}
              </Badge>
            ))
          ) : (
            <span className="text-sm text-gray-500">No scope selected</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Scope is set from the organization operating model and can be changed by a platform admin.
        </p>
      </FieldGroup>

      <FieldGroup label="Q5. Country of primary operation">
        <Select
          value={data.country_of_operation}
          onValueChange={(v) => setData({ ...data, country_of_operation: v })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_OPTIONS.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldGroup>

      <RepeatableField
        label="Q6. Office / operational centre locations"
        values={data.office_locations}
        onChange={(v) => setData({ ...data, office_locations: v })}
        placeholder="e.g. London, Aberdeen, etc."
      />

      <MultiCheckboxField
        label="Q7. Primary scope of rope access work"
        options={SCOPE_OPTIONS}
        selected={displayScope}
        onChange={handleScopeChange}
      />
      {scopeHasOther && (
        <div className="ml-6">
          <Input
            value={scopeOtherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            placeholder="Please describe your 'Other' scope of work"
            className="max-w-md"
          />
        </div>
      )}

      <MultiCheckboxField
        label="Q8. Work environments"
        options={ENVIRONMENT_OPTIONS}
        selected={data.work_environments}
        onChange={(v) => setData({ ...data, work_environments: v })}
      />

      <h3 className="text-md font-semibold pt-4 border-t">Legal Entity Details</h3>

      <FieldGroup label="Registered entity name">
        <Input
          value={data.legal_entity_registered_name}
          onChange={(e) => setData({ ...data, legal_entity_registered_name: e.target.value })}
          placeholder="As it appears on the certificate of incorporation"
        />
      </FieldGroup>

      <FieldGroup label="Company registration / incorporation number">
        <Input
          value={data.legal_entity_registration_number}
          onChange={(e) => setData({ ...data, legal_entity_registration_number: e.target.value })}
          placeholder="e.g. 12345678"
        />
      </FieldGroup>

      <FieldGroup label="Country of registration">
        <Select
          value={data.legal_entity_country}
          onValueChange={(v) => setData({ ...data, legal_entity_country: v })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select country" />
          </SelectTrigger>
          <SelectContent>
            {COUNTRY_OPTIONS.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldGroup>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Section
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 2 — Nominated Personnel
// ---------------------------------------------------------------------------

function Section2NominatedPersonnel({ profile, onSave, isSaving, sectionDataRef }: SectionProps) {
  const { data: techProfilesData, isLoading: techProfilesLoading } = useTechProfiles({ page_size: 200 });
  const techProfiles = techProfilesData?.results ?? [];
  const profilesKey = techProfilesLoading ? 'loading' : 'loaded';

  const [data, setData] = useState({
    ramr_name: profile.ramr_name || '',
    ramr_user: profile.ramr_user ?? null,
    ramr_irata_level: profile.ramr_irata_level || '',
    technical_authority_name: profile.technical_authority_name || '',
    technical_authority_user: profile.technical_authority_user ?? null,
    technical_authority_cert_number: profile.technical_authority_cert_number || '',
    technical_authority_cert_expiry: profile.technical_authority_cert_expiry || null,
    primary_contact_name: profile.primary_contact_name || '',
    equipment_selector_name: profile.equipment_selector_name || '',
    equipment_inspector_name: profile.equipment_inspector_name || '',
    safety_returns_name: profile.safety_returns_name || '',
    safety_policy_signatory_name: profile.safety_policy_signatory_name || '',
    safety_policy_signatory_title: profile.safety_policy_signatory_title || '',
    insurance_insurer: profile.insurance_insurer || '',
    insurance_policy_number: profile.insurance_policy_number || '',
    insurance_expiry: profile.insurance_expiry || null,
    insurance_covers_rope_access: profile.insurance_covers_rope_access ?? null,
  });

  useEffect(() => { sectionDataRef.current = () => data; }, [data, sectionDataRef]);

  const handleSave = () => onSave(data);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Section 2 — Nominated Personnel</h2>

      <PeopleCombobox
        key={`ramr-${profilesKey}`}
        label="Q9. Rope Access Management Representative (RAMR)"
        nameValue={data.ramr_name}
        onSelect={(name, techProfileId) =>
          setData({ ...data, ramr_name: name, ramr_user: techProfileId })
        }
        techProfiles={techProfiles}
        required
        irataLevel={data.ramr_irata_level}
        onIrataLevelChange={(v) => setData({ ...data, ramr_irata_level: v })}
      />

      <PeopleCombobox
        key={`ta-${profilesKey}`}
        label="Q10. Technical Authority"
        nameValue={data.technical_authority_name}
        onSelect={(name, techProfileId) =>
          setData({ ...data, technical_authority_name: name, technical_authority_user: techProfileId })
        }
        techProfiles={techProfiles}
        description="Must hold or have held IRATA Level 3"
        required
      />

      <FieldGroup label="Technical Authority — IRATA certification number">
        <Input
          value={data.technical_authority_cert_number}
          onChange={(e) => setData({ ...data, technical_authority_cert_number: e.target.value })}
          placeholder="e.g. 12345"
        />
      </FieldGroup>

      <FieldGroup label="Technical Authority — certification expiry">
        <Input
          type="date"
          value={data.technical_authority_cert_expiry ?? ''}
          onChange={(e) => setData({ ...data, technical_authority_cert_expiry: e.target.value || null })}
        />
      </FieldGroup>

      <PersonnelField
        label="Q11. Primary company contact"
        nameValue={data.primary_contact_name}
        onNameChange={(v) => setData({ ...data, primary_contact_name: v })}
      />

      <PersonnelField
        label="Q12. Responsible for equipment selection"
        nameValue={data.equipment_selector_name}
        onNameChange={(v) => setData({ ...data, equipment_selector_name: v })}
      />

      <PersonnelField
        label="Q13. Responsible for detailed equipment inspections"
        nameValue={data.equipment_inspector_name}
        onNameChange={(v) => setData({ ...data, equipment_inspector_name: v })}
      />

      <PersonnelField
        label="Q14. Responsible for submitting quarterly IRATA safety returns"
        nameValue={data.safety_returns_name}
        onNameChange={(v) => setData({ ...data, safety_returns_name: v })}
      />

      <FieldGroup label="Q15. Top management signatory for the Safety Policy">
        <div className="flex gap-2">
          <Input
            value={data.safety_policy_signatory_name}
            onChange={(e) => setData({ ...data, safety_policy_signatory_name: e.target.value })}
            placeholder="Full name"
            className="flex-1"
          />
          <Input
            value={data.safety_policy_signatory_title}
            onChange={(e) => setData({ ...data, safety_policy_signatory_title: e.target.value })}
            placeholder="Job title"
            className="flex-1"
          />
        </div>
      </FieldGroup>

      <h3 className="text-md font-semibold pt-4 border-t">Insurance</h3>

      <FieldGroup label="Insurance provider">
        <Input
          value={data.insurance_insurer}
          onChange={(e) => setData({ ...data, insurance_insurer: e.target.value })}
          placeholder="Name of your insurance company"
        />
      </FieldGroup>

      <FieldGroup label="Policy number">
        <Input
          value={data.insurance_policy_number}
          onChange={(e) => setData({ ...data, insurance_policy_number: e.target.value })}
          placeholder="e.g. POL-12345"
        />
      </FieldGroup>

      <FieldGroup label="Policy expiry date">
        <Input
          type="date"
          value={data.insurance_expiry ?? ''}
          onChange={(e) => setData({ ...data, insurance_expiry: e.target.value || null })}
        />
      </FieldGroup>

      <FieldGroup label="Does your policy specifically cover rope access work?">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.insurance_covers_rope_access === true}
              onChange={() => setData({ ...data, insurance_covers_rope_access: true })}
              className="text-blue-600"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.insurance_covers_rope_access === false}
              onChange={() => setData({ ...data, insurance_covers_rope_access: false })}
              className="text-blue-600"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
      </FieldGroup>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Section
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 3 — Operations & Supervision
// ---------------------------------------------------------------------------

function Section3Operations({ profile, onSave, isSaving, sectionDataRef }: SectionProps) {
  const [data, setData] = useState({
    technician_irata_levels: profile.technician_irata_levels || [],
    level3_on_site: profile.level3_on_site ?? true,
    level3_on_site_explanation: profile.level3_on_site_explanation || '',
    certification_verification: profile.certification_verification || '',
    uses_subcontractors: profile.uses_subcontractors ?? false,
  });

  useEffect(() => { sectionDataRef.current = () => data; }, [data, sectionDataRef]);

  const handleSave = () => onSave(data);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Section 3 — Operations & Supervision</h2>

      <MultiCheckboxField
        label="Q16. What IRATA levels do your technicians hold?"
        options={IRATA_LEVEL_OPTIONS}
        selected={data.technician_irata_levels}
        onChange={(v) => setData({ ...data, technician_irata_levels: v })}
      />

      <FieldGroup label="Q17. Is every job directly supervised by a qualified IRATA Level 3 on site?">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.level3_on_site}
              onChange={() => setData({ ...data, level3_on_site: true })}
              className="text-blue-600"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={!data.level3_on_site}
              onChange={() => setData({ ...data, level3_on_site: false })}
              className="text-blue-600"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
        {!data.level3_on_site && (
          <Textarea
            value={data.level3_on_site_explanation}
            onChange={(e) => setData({ ...data, level3_on_site_explanation: e.target.value })}
            placeholder="Please explain your supervision arrangement"
            className="mt-2"
          />
        )}
      </FieldGroup>

      <FieldGroup label="Q18. How do you verify technician certifications are current before deployment?">
        <Textarea
          value={data.certification_verification}
          onChange={(e) => setData({ ...data, certification_verification: e.target.value })}
          placeholder="e.g. checked via IRATA website, maintained in personnel files"
        />
      </FieldGroup>

      <FieldGroup label="Q19. Do you use subcontractors or third party technicians?">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.uses_subcontractors}
              onChange={() => setData({ ...data, uses_subcontractors: true })}
              className="text-blue-600"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={!data.uses_subcontractors}
              onChange={() => setData({ ...data, uses_subcontractors: false })}
              className="text-blue-600"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
        {data.uses_subcontractors && (
          <Badge variant="secondary" className="mt-2">
            Auto-text: &quot;We establish controls for risk management when working with third
            party technicians and verify their IRATA certification prior to engagement&quot;
          </Badge>
        )}
      </FieldGroup>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Section
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 4 — Job Planning & Communication
// ---------------------------------------------------------------------------

function Section4JobPlanning({ profile, onSave, isSaving, sectionDataRef }: SectionProps) {
  const [data, setData] = useState({
    pre_job_documents: profile.pre_job_documents || [],
    supervisor_briefing: profile.supervisor_briefing || '',
    safety_communication_method: profile.safety_communication_method || '',
    workforce_consultation: profile.workforce_consultation || '',
  });

  useEffect(() => { sectionDataRef.current = () => data; }, [data, sectionDataRef]);

  const handleSave = () => onSave(data);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Section 4 — Job Planning & Communication</h2>

      <MultiCheckboxField
        label="Q20. What documents are prepared before every job?"
        options={PRE_JOB_DOCUMENT_OPTIONS}
        selected={data.pre_job_documents}
        onChange={(v) => setData({ ...data, pre_job_documents: v })}
      />

      <FieldGroup label="Q21. How are supervisors briefed on work scope before mobilisation?">
        <Textarea
          value={data.supervisor_briefing}
          onChange={(e) => setData({ ...data, supervisor_briefing: e.target.value })}
          placeholder="Describe your briefing process"
        />
      </FieldGroup>

      <FieldGroup label="Q22. How is safety information communicated to the team on site?">
        <Textarea
          value={data.safety_communication_method}
          onChange={(e) => setData({ ...data, safety_communication_method: e.target.value })}
          placeholder="Describe your communication methods"
        />
      </FieldGroup>

      <FieldGroup label="Q23. How do you consult with your workforce on rope access controls?">
        <Textarea
          value={data.workforce_consultation}
          onChange={(e) => setData({ ...data, workforce_consultation: e.target.value })}
          placeholder="Describe your consultation process"
        />
      </FieldGroup>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Section
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 5 — Equipment Management
// ---------------------------------------------------------------------------

function Section5Equipment({ profile, onSave, isSaving, sectionDataRef }: SectionProps) {
  const [data, setData] = useState({
    equipment_selection_process: profile.equipment_selection_process || '',
    detailed_inspection_frequency: profile.detailed_inspection_frequency || '6_monthly',
    interim_inspection_process: profile.interim_inspection_process || '',
    failed_inspection_process: profile.failed_inspection_process || '',
    equipment_marking_process: profile.equipment_marking_process || '',
  });

  useEffect(() => { sectionDataRef.current = () => data; }, [data, sectionDataRef]);

  const handleSave = () => onSave(data);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Section 5 — Equipment Management</h2>

      <FieldGroup label="Q24. How is rope access equipment selected and purchased?">
        <Textarea
          value={data.equipment_selection_process}
          onChange={(e) => setData({ ...data, equipment_selection_process: e.target.value })}
          placeholder="e.g. selected by competent person, sourced from authorised distributors"
        />
      </FieldGroup>

      <FieldGroup label="Q25. How often are detailed equipment inspections carried out?">
        <Select
          value={data.detailed_inspection_frequency}
          onValueChange={(v) => setData({ ...data, detailed_inspection_frequency: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="6_monthly">6 monthly (IRATA minimum)</SelectItem>
            <SelectItem value="3_monthly">3 monthly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="other">Other (specify below)</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>

      <FieldGroup label="Q26. How are interim inspections managed between detailed inspections?">
        <Textarea
          value={data.interim_inspection_process}
          onChange={(e) => setData({ ...data, interim_inspection_process: e.target.value })}
          placeholder="Describe your interim inspection process"
        />
      </FieldGroup>

      <FieldGroup label="Q27. How is equipment that fails inspection handled?">
        <Textarea
          value={data.failed_inspection_process}
          onChange={(e) => setData({ ...data, failed_inspection_process: e.target.value })}
          placeholder="Describe your quarantine process"
        />
      </FieldGroup>

      <FieldGroup label="Q28. How is equipment marked and traced to inspection records?">
        <Textarea
          value={data.equipment_marking_process}
          onChange={(e) => setData({ ...data, equipment_marking_process: e.target.value })}
          placeholder="Describe your marking and tracing process"
        />
      </FieldGroup>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Section
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 6 — Emergency & Incident Management
// ---------------------------------------------------------------------------

function Section6Emergency({ profile, onSave, isSaving, sectionDataRef }: SectionProps) {
  const [data, setData] = useState({
    site_specific_rescue_plan: profile.site_specific_rescue_plan ?? true,
    emergency_response_person_name: profile.emergency_response_person_name || '',
    incident_reporting_process: profile.incident_reporting_process || '',
    irata_incident_commitment: profile.irata_incident_commitment ?? false,
    incident_investigation_process: profile.incident_investigation_process || '',
  });

  useEffect(() => { sectionDataRef.current = () => data; }, [data, sectionDataRef]);

  const handleSave = () => onSave(data);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Section 6 — Emergency & Incident Management</h2>

      <FieldGroup label="Q29. Does every job have a site-specific rescue plan?">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.site_specific_rescue_plan}
              onChange={() => setData({ ...data, site_specific_rescue_plan: true })}
              className="text-blue-600"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={!data.site_specific_rescue_plan}
              onChange={() => setData({ ...data, site_specific_rescue_plan: false })}
              className="text-blue-600"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
      </FieldGroup>

      <PersonnelField
        label="Q30. Who is responsible for emergency response planning?"
        nameValue={data.emergency_response_person_name}
        onNameChange={(v) => setData({ ...data, emergency_response_person_name: v })}
      />

      <FieldGroup label="Q31. How are incidents reported internally?">
        <Textarea
          value={data.incident_reporting_process}
          onChange={(e) => setData({ ...data, incident_reporting_process: e.target.value })}
          placeholder="Describe your incident reporting process"
        />
      </FieldGroup>

      <FieldGroup label="Q32. Do you understand and commit to reporting all incidents to IRATA within 7 days?">
        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={data.irata_incident_commitment}
            onCheckedChange={(checked) =>
              setData({ ...data, irata_incident_commitment: checked === true })
            }
          />
          <span className="text-sm">
            I confirm we understand and commit to reporting all incidents to IRATA within 7 days
          </span>
        </label>
      </FieldGroup>

      <FieldGroup label="Q33. How are incidents investigated?">
        <Textarea
          value={data.incident_investigation_process}
          onChange={(e) => setData({ ...data, incident_investigation_process: e.target.value })}
          placeholder="Describe your root cause investigation process"
        />
      </FieldGroup>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Section
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 7 — Compliance & Legal
// ---------------------------------------------------------------------------

function Section7Legal({ profile, onSave, isSaving, sectionDataRef }: SectionProps) {
  const suggestions = profile.country_legislation_suggestions || [];
  const [data, setData] = useState({
    applicable_legislation: profile.applicable_legislation?.length
      ? profile.applicable_legislation
      : suggestions.length
        ? [...suggestions]
        : [''],
    legal_communication_method: profile.legal_communication_method || '',
    non_legal_standards: profile.non_legal_standards?.length
      ? profile.non_legal_standards
      : ['IRATA ICOP', 'IRATA TACS'],
    non_legal_communication_method: profile.non_legal_communication_method || '',
  });

  useEffect(() => { sectionDataRef.current = () => data; }, [data, sectionDataRef]);

  const handleSave = () => onSave(data);

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Section 7 — Compliance & Legal</h2>

      <RepeatableField
        label="Q34. What national legislation applies to your rope access operations?"
        values={data.applicable_legislation}
        onChange={(v) => setData({ ...data, applicable_legislation: v })}
        placeholder="e.g. Health and Safety at Work Act 1974"
      />
      {suggestions.length > 0 && (
        <p className="text-xs text-gray-500">
          Pre-populated based on country selection. Edit or add as needed.
        </p>
      )}

      <FieldGroup label="Q35. How are legal requirements communicated to workers?">
        <Textarea
          value={data.legal_communication_method}
          onChange={(e) => setData({ ...data, legal_communication_method: e.target.value })}
          placeholder="Describe your communication methods"
        />
      </FieldGroup>

      <RepeatableField
        label="Q36. What non-legal standards and codes does your company follow?"
        values={data.non_legal_standards}
        onChange={(v) => setData({ ...data, non_legal_standards: v })}
        placeholder="e.g. IRATA ICOP, IRATA TACS"
      />

      <FieldGroup label="Q37. How are non-legal requirements communicated to workers?">
        <Textarea
          value={data.non_legal_communication_method}
          onChange={(e) => setData({ ...data, non_legal_communication_method: e.target.value })}
          placeholder="Describe your communication methods"
        />
      </FieldGroup>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Section
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section 8 — Review & Audit
// ---------------------------------------------------------------------------

function Section8Review({ profile, onSave, isSaving, sectionDataRef }: SectionProps) {
  const [data, setData] = useState({
    internal_audit_frequency: profile.internal_audit_frequency || 'annually',
    internal_auditor: profile.internal_auditor || '',
    management_review_frequency: profile.management_review_frequency || 'annually',
    next_assessment_date: profile.next_assessment_date || '',
    tracks_jsas_in_h3safe: profile.tracks_jsas_in_h3safe ?? null,
    tracks_assets_in_h3gear: profile.tracks_assets_in_h3gear ?? null,
    tracks_rescue_plans_in_h3safe: profile.tracks_rescue_plans_in_h3safe ?? null,
  });

  useEffect(() => {
    sectionDataRef.current = () => ({
      ...data,
      next_assessment_date: data.next_assessment_date || null,
    });
  }, [data, sectionDataRef]);

  const handleSave = () => onSave({
    ...data,
    next_assessment_date: data.next_assessment_date || null,
  });

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Section 8 — Review & Audit</h2>

      <FieldGroup label="Q38. How frequently do you conduct internal audits of your rope access management system?">
        <Select
          value={data.internal_audit_frequency}
          onValueChange={(v) => setData({ ...data, internal_audit_frequency: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="annually">Annually (IRATA minimum)</SelectItem>
            <SelectItem value="semi_annually">Semi-annually</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>

      <FieldGroup label="Q39. Who conducts your internal audits?">
        <Textarea
          value={data.internal_auditor}
          onChange={(e) => setData({ ...data, internal_auditor: e.target.value })}
          placeholder="Name or role of the auditor"
        />
      </FieldGroup>

      <FieldGroup label="Q40. How frequently does top management review safety performance?">
        <Select
          value={data.management_review_frequency}
          onValueChange={(v) => setData({ ...data, management_review_frequency: v })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="annually">Annually</SelectItem>
            <SelectItem value="semi_annually">Semi-annually</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
          </SelectContent>
        </Select>
      </FieldGroup>

      <FieldGroup label="Q41. When is your next scheduled IRATA assessment?">
        <Input
          type="date"
          value={data.next_assessment_date}
          onChange={(e) => setData({ ...data, next_assessment_date: e.target.value })}
          className="max-w-xs"
        />
        <p className="text-sm text-muted-foreground mt-1">
          This powers the Audit Readiness countdown on the operations board.
        </p>
      </FieldGroup>

      <h3 className="text-md font-semibold pt-4 border-t">H3 Platform Integration</h3>
      <p className="text-sm text-muted-foreground">
        Let us know which H3 modules your organisation uses so the assessor can
        pull live data instead of requiring separate documentary evidence.
      </p>

      <FieldGroup label="Do you use H3Safe to manage JSAs / risk assessments?">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.tracks_jsas_in_h3safe === true}
              onChange={() => setData({ ...data, tracks_jsas_in_h3safe: true })}
              className="text-blue-600"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.tracks_jsas_in_h3safe === false}
              onChange={() => setData({ ...data, tracks_jsas_in_h3safe: false })}
              className="text-blue-600"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
      </FieldGroup>

      <FieldGroup label="Do you use H3Gear to manage assets / equipment?">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.tracks_assets_in_h3gear === true}
              onChange={() => setData({ ...data, tracks_assets_in_h3gear: true })}
              className="text-blue-600"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.tracks_assets_in_h3gear === false}
              onChange={() => setData({ ...data, tracks_assets_in_h3gear: false })}
              className="text-blue-600"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
      </FieldGroup>

      <FieldGroup label="Do you use H3Safe to manage rescue plans?">
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.tracks_rescue_plans_in_h3safe === true}
              onChange={() => setData({ ...data, tracks_rescue_plans_in_h3safe: true })}
              className="text-blue-600"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              checked={data.tracks_rescue_plans_in_h3safe === false}
              onChange={() => setData({ ...data, tracks_rescue_plans_in_h3safe: false })}
              className="text-blue-600"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
      </FieldGroup>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Save Section
        </Button>
      </div>
    </div>
  );
}
